import { Children, cloneElement, FC, forwardRef, HTMLProps, isValidElement, ReactElement, ReactNode, Ref } from 'react';
import cn from 'classnames';

import { ButtonProps } from '../../molecules/button/Button';
import { Container, ContainerBgColors, ContainerProps } from '../../../components/atoms/container/Container';
import { Image } from '../../../components/atoms/image/Image';
import { Heading } from '../../../components/atoms/heading/Heading';
import '../../assets/styles/utilities.scss';
import './Card.scss';

const classCard = 'lsux-card';

export type PaddingType = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;

export interface CardProps extends Omit<HTMLProps<HTMLDivElement>, 'onClick' | 'css'> {
  children?: ReactNode | ReactNode[];
  /**
   * Define card height
   */
  cardHeight?: string;
  /**
   * Defines card width.
   */
  cardWidth?: string;
  /**
   * Define if container has background
   * @default white
   */
  background?: ContainerBgColors;
  /**
   * Determines if the card can be selected or not. This adds selectable styling and pointer.
   */
  selectable?: boolean;
  /**
   * Determines if the card is selected or not
   */
  isSelected?: boolean;
  /**
   * Additional class names
   */
  classNames?: cn.Argument;
  /**
   * The ref to the HTML DOM element.
   */
  ref?: Ref<HTMLDivElement>;
  /**
   * Callback function when card is clicked
   */
  onClick?: (id: string) => void;
}

export interface CardActionsProps {
  /**
   * Alignment of the buttons
   * @default left
   */
  align?: 'left' | 'center' | 'right';
  /**
   * Buttons to be displayed
   */
  children: ReactElement | ReactElement[];
}
export const CardActions: FC<CardActionsProps> = ({ children, align = 'left' }: CardActionsProps) => {
  const childrenWithProps = Children.map(children as ReactElement<ButtonProps>[], (child) => {
    if (isValidElement(child)) {
      return cloneElement(child, { shape: 'round', textSize: 'medium', variant: 'tertiary' });
    }
    return child;
  });

  const alignMap: Record<string, string> = {
    center: 'justify-content-center',
    left: 'justify-content-start',
    right: 'justify-content-end',
  };

  const cnCardAction = cn(`${classCard}--card-actions`, alignMap[align]);

  return <div className={cnCardAction}>{childrenWithProps}</div>;
};

export interface CardContentProps extends ContainerProps {
  /**
   * Define if container has background
   */
  background?: ContainerBgColors;
  /**
   * Elements to show in the card content
   */
  children: ReactElement | ReactElement[];
  /**
   * Additional class names
   */
  classNames?: cn.Argument;
  /**
   * Padding for the content
   */
  padding?: number;
}
export const CardContent: FC<CardContentProps> = ({
  children,
  classNames = [],
  padding = 4,
  background,
  ...props
}: CardContentProps) => {
  const cnCardContent: cn.Argument = [`${classCard}__content`, `p-${padding}`, classNames];
  return (
    <Container {...props} background={background} classNames={cnCardContent}>
      {children}
    </Container>
  );
};

export interface CardImageProps {
  imageURL: string;
  imageAltText?: string;
  classNames?: cn.Argument;
}
export const CardImage: FC<CardImageProps> = ({ imageURL, imageAltText, classNames = [] }: CardImageProps) => {
  const cnCardImage = cn(`${classCard}__image`, classNames);
  return (
    <div className={cnCardImage}>
      <Image src={imageURL} alt={imageAltText} fluid />
    </div>
  );
};

export interface CardTitleProps {
  /**
   * Title of the card
   */
  children: string;
  /**
   * Additional class names
   */
  classNames?: cn.Argument;
  /**
   * Card title height
   */
  titleHeight?: string;
}
export const CardTitle: FC<CardTitleProps> = ({ children, classNames = [], titleHeight }: CardTitleProps) => {
  const cnCardTitle = cn(`${classCard}__title`, classNames);
  return (
    <div className={cnCardTitle}>
      <Heading as="T16" text={children} titleHeight={titleHeight} classNames={['pb-3']} />
    </div>
  );
};

/**
 * Cards are flexible, rectangular containers for text, images, or actions for a single topic. Cards are intended to be flexible across device widths and should live within our grid and defined layout.
 */
const CardComponent = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      onClick = null,
      children,
      classNames = [],
      cardHeight,
      cardWidth,
      background,
      isSelected,
      selectable = isSelected !== undefined ?? false,
      ...props
    },
    ref: CardProps['ref'],
  ) => {
    const handleCardClick = (): void => {
      if (onClick != null) {
        onClick(props.id);
      }
    };

    const cnCard = cn(
      {
        [`${classCard}`]: true,
        [`${classCard}--bg--${background ?? 'white'}`]: true,
        [`${classCard}--selectable`]: selectable,
        [`${classCard}--selected`]: isSelected,
        [`${classCard}--unselected`]: !isSelected,
      },
      classNames,
    );

    const cardStyle = {
      height: cardHeight,
      width: cardWidth,
    };

    return (
      <div {...props} ref={ref} onClick={handleCardClick} className={cnCard} style={{ ...cardStyle, ...props.style }}>
        {children}
      </div>
    );
  },
) as FC<CardProps>;

CardComponent.displayName = 'Card';

type CardActionsComponent = FC<CardActionsProps>;
type CardContentComponent = FC<CardContentProps>;
type CardImageComponent = FC<CardImageProps>;
type CardTitleComponent = FC<CardTitleProps>;
type CardComp = FC<CardProps> & {
  Actions: CardActionsComponent;
  Content: CardContentComponent;
  Image: CardImageComponent;
  Title: CardTitleComponent;
};

export const Card = CardComponent as CardComp;

Card.Actions = CardActions;
Card.Content = CardContent;
Card.Image = CardImage;
Card.Title = CardTitle;
